// utils/pendingPrompt.js — the question a visitor typed on the landing page,
// kept through sign-up and asked as their first question once they are in.
//
// The landing page's chat box used to send a signed-out visitor to /register
// with the question in the router's state, and nothing ever read it: sign-up
// goes on to /dashboard, and the question was gone. It lives in this tab's
// sessionStorage now (a Google redirect sign-up comes back to the same tab),
// and the home chat asks it when it is free: after the setup, if a new
// account is still on it (AntigravitySidebar.js).
import { useEffect, useRef } from 'react';

const KEY = 'assistalien.firstQuestion';
const MAX = 2000;

/** Keep what the visitor typed. Blank is not kept. */
export function keepPendingPrompt(text) {
  const q = String(text || '').trim().slice(0, MAX);
  if (!q) return;
  try { sessionStorage.setItem(KEY, q); } catch { /* private mode: the question is lost, nothing else */ }
}

/** What is waiting, without taking it (the sign-up page says it is saved). */
export function peekPendingPrompt() {
  try { return sessionStorage.getItem(KEY) || null; } catch { return null; }
}

/** Take it: it is asked once, never twice. */
export function takePendingPrompt() {
  try {
    const q = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
    return q && q.trim() ? q : null;
  } catch {
    return null;
  }
}

/**
 * Ask the waiting question with `send` as soon as `ready` is true, once.
 * `ready` is the home chat being free: signed in, empty, setup not showing.
 */
export function useFirstQuestion(ready, send) {
  const sent = useRef(false);
  useEffect(() => {
    if (!ready || sent.current) return;
    const q = takePendingPrompt();
    if (!q) return;
    sent.current = true;
    send(q);
  }, [ready, send]);
}
